/* eslint-disable react-func/max-lines-per-function */
/* eslint-disable max-lines */
import { v4 as uuid } from 'uuid';
import { isNil, isObject, isString } from 'lodash-es';
import { CheckType } from '@/constants';
import { CheckItem, CheckItemProps, FileCheckRuleItem, RuleItemProps, TabItemSource } from 'common/types';
import { TabDomProps } from '@/store/DccJsonToTabs/dynamicCheckItemConfig';
import service from '@/service';
import { formatByteByUnit, formatSizeToByte } from './utils';

/**
 * 数据格式：
 * 1.任务模版的checkList：一级数组
 * 2.配置页面的表单数据源 tabsSource：二级数组
 *
 * 数据操作：（用户操作划分）
 * 1.tab增删-checkItem增删
 * 2.动态扩展配置DynamicSettingCard的增删-checkItem增删
 * 3.rule变动-checkItem.rules变化（增删改、表单校验）
 * 4.特殊rule变动-checkItem.fileName、missing变化
 *
 *
 * 数据操作顺序：
 * 0.服务器存储-任务模版的checkList
 * 1.编辑子任务，获取对应-checkList，按照规则解析生成tabsSource @func parseCheckListToTabsSource
 * 2.DOM层级向下分发tabsSource数据，渲染form DOM
 * 3.form DOM维护数据状态form value @component DynamicSetting/NormalSetting
 * 4.并更新对应tabSource
 * 5.子任务切换时，tabSource生成最新checkList
 */

/**
 * 解析 任务模版的checkItem数据 =》配置页面的tab结构
 * @param checkItem
 * @returns TabItemSource
 */
export const initTabItemSource = (
  checkItem: Partial<CheckItem>,
  isSubItem: boolean,
  checkItemConfig: CheckItemProps,
): TabItemSource => {
  const ruleForm: RuleItemProps[] = checkItemConfig?.ruleForm || [];
  const formValues = {
    [`${CheckType.FileName}`]: checkItem?.[`${CheckType.FileName}`],
    [`${CheckType.Name}`]: checkItem?.[`${CheckType.Name}`],
    [`${CheckType.FileNameDesc}`]: checkItem?.[`${CheckType.FileNameDesc}`],
  };
  const isChosenChecks: Array<string> = [];
  const checkMethods = {};
  const ruleIds = {};
  const checkItemType = checkItem.type || '';
  const itemId = checkItem?.itemId || uuid();
  let fileTypes = checkItem?.fileType || [];
  const id = checkItem.id;
  const children = checkItem?.children || [];
  checkItem?.rules?.forEach((item) => {
    const { ruleType, isShowRule, checkValue, checkMethod, id } = item || {};
    if (isShowRule) {
      isChosenChecks.push(ruleType);
    }
    if (checkValue) {
      const ruleConfig = ruleForm?.find((item) => item?.ruleType === ruleType);
      const formatValue = formatCheckValueForFrontEnd(ruleType, checkValue, ruleConfig);
      Object.assign(formValues, { [`${ruleType}`]: formatValue });
    }
    if (checkMethod) {
      Object.assign(checkMethods, { [`${ruleType}`]: checkMethod });
    }
    if (id) {
      Object.assign(ruleIds, { [`${ruleType}`]: id });
    }
  });
  Object.assign(
    formValues,
    { [`${CheckType.IsRequired}`]: checkItem?.missing },
    { [`${CheckType.Name}`]: checkItem?.name },
    { [`${CheckType.FileName}`]: checkItem?.fileName },
  );
  return {
    checkItemType,
    itemId,
    checkMethods,
    fileTypes,
    formValues,
    ruleIds,
    isChosenChecks,
    children,
    isSubItem: !!isSubItem,
    id,
  };
};

/**
 * 动态解析 任务模版的checkList数据 =》配置页面的tabs 结构
 * @param checkList 任务模版的checkList
 * @param tabsDom 当前动态配置规则缓存
 * @return tabsSource TabItemSource[]
 * @return tabsDom TabDomProps[] 配置页面的动态配置规则
 */
export async function asyncParseCheckListToTabsSource(
  projectId: string | number,
  tabsDom: TabDomProps[],
  checkList?: CheckItem[],
): Promise<{ tabsSource: TabItemSource[]; tabsDom: TabDomProps[] }> {
  let tabsSource: TabItemSource[] = [];
  if (!checkList?.length) {
    return { tabsSource, tabsDom };
  }
  let currentTabsDom: TabDomProps[] = tabsDom;
  // 解析checklist
  await Promise.all(
    checkList?.map(async (checkItem) => {
      const isFile = !!checkItem?.fileName;
      if (isFile) {
        // 处理主文件，获取对应的动态配置
        const type = checkItem?.type;
        const fileType = checkItem?.fileType;
        let tab: TabItemSource | undefined = undefined;
        //包含嵌套关系的子检查项
        let tabDom: TabDomProps | undefined = undefined;
        currentTabsDom?.forEach((tab) => {
          const isCached = tab.type === type && tab?.fileType?.[0] === fileType?.[0];
          isCached && (tabDom = tab);
        });
        if (!tabDom) {
          // 请求并缓存规则
          const config = (await service.dynamicCheckItemSetting.getCheckItemConfig(type, fileType!, projectId)) || [];
          tabDom = { config, type, fileType } as TabDomProps;
          currentTabsDom = currentTabsDom.concat(tabDom);
        }
        const childItems: CheckItem[] =
          checkItem?.children
            ?.map?.((itemId) => {
              return checkList?.find((item) => item.itemId === itemId);
            })
            ?.filter?.((item) => item !== undefined) || ([] as any);
        const subItems: TabItemSource[] = [];
        tabDom?.config?.forEach?.((checkItemConfig: CheckItemProps) => {
          const { checkItemType } = checkItemConfig;
          const isSubItem = checkItemType !== type;
          if (isSubItem) {
            childItems
              ?.filter(({ type }) => type === checkItemType)
              ?.forEach((item) => {
                subItems.push(initTabItemSource(item, isSubItem, checkItemConfig));
              });
          } else {
            tab = initTabItemSource(checkItem, isSubItem, checkItemConfig);
          }
        });

        if (tab !== undefined) {
          Object.assign(tab, { subItems });
          tabsSource = tabsSource.concat(tab);
        }
      }
    }),
  );
  return { tabsSource, tabsDom: currentTabsDom };
}

/**
 * 动态解析 配置页面的tabs 结构 + 表单录入数据 =》任务模版的checkList数据
 * @param TabItemSource[]
 * @param tabsDom 当前动态配置规则缓存
 * @returns checkList 任务模版的checkList
 * @return tabsDom TabDomProps[] 配置页面的动态配置规则
 */
export async function asyncParseTabsSourceToCheckList(
  projectId: string | number,
  tabs: TabItemSource[],
  tabsDom: TabDomProps[],
): Promise<{ checkList: CheckItem[]; tabsDom: TabDomProps[] }> {
  const checkList: CheckItem[] = [];
  let currentTabsDom = tabsDom;
  await Promise.all(
    tabs?.map(async (tab) => {
      const { checkItemType: type, fileTypes: fileType } = tab;
      let tabDom: TabDomProps | undefined = undefined;
      currentTabsDom?.forEach((tab) => {
        const isCached = tab.type === type && tab?.fileType?.[0] === fileType?.[0];
        isCached && (tabDom = tab);
      });
      if (!tabDom) {
        // 请求并缓存规则
        const config = (await service.dynamicCheckItemSetting.getCheckItemConfig(type, fileType!, projectId)) || [];
        tabDom = { config, type, fileType } as TabDomProps;
        currentTabsDom = currentTabsDom.concat(tabDom);
      }
      tabDom?.config?.forEach?.((checkItemConfig: CheckItemProps) => {
        const { checkItemType } = checkItemConfig;
        const isSubItem = checkItemType !== type;
        if (isSubItem) {
          tab?.subItems
            ?.filter((item) => checkItemType === item?.checkItemType)
            ?.forEach((item) => {
              const subCheckItem = Object.assign(parseFormValuesToCheckItem(item, checkItemConfig?.ruleForm), {
                type: item?.checkItemType,
                fileType: item?.fileTypes,
              });
              checkList.push(subCheckItem);
            });
        } else {
          const basicItem: CheckItem = Object.assign(parseFormValuesToCheckItem(tab, checkItemConfig?.ruleForm), {
            type,
            fileType,
          });
          checkList.push(basicItem);
        }
      });
    }),
  );
  return { checkList, tabsDom: currentTabsDom };
}
/**
 * 表单录入规则数据 + 动态配置规则 =》ruleItem json
 * @param formValues 表单录入数据
 * @param ruleForm 动态配置规则
 * @returns {}
 */
export function getRuleItemJson(
  ruleType: string,
  ruleForm: RuleItemProps[],
  value,
  checkMethod,
  isShowRule,
  ruleId?: number,
): FileCheckRuleItem {
  const rule = ruleForm?.find((item) => item.ruleType === ruleType);
  const checkValue = formatCheckValueForBackEnd(ruleType, value, rule);
  const res = {
    ruleType,
    ruleLabel: rule?.title!,
    submitMessage: rule?.submitMessage,
    checkMessage: rule?.checkMessage,
    checkValue,
    isShowRule, // 只质检不输出pass/not_pass
    checkMethod: checkMethod || rule?.checkMethods?.[0],
    id: ruleId,
  };
  // console.log({ res, ruleForm, rule, checkMethod });
  return res;
}

/**
 * 表单录入数据集合 + 动态配置规则 =》任务模版的checkList子数据 配置页面的某checkItem数据、isChosen、error校验
 * @param TabItemSource 表单录入数据
 * @param ruleForm 动态配置规则
 * @returns {}
 */
export function parseFormValuesToCheckItem(item: TabItemSource, ruleForm: RuleItemProps[]) {
  const { formValues, checkMethods, ruleIds } = item;
  const inputRules = item.isChosenChecks; // 勾选的规则
  const checkItem = {
    itemId: item.itemId,
    children: item?.subItems?.map?.((sub) => sub.itemId),
    name: '',
    fileName: '',
  };
  const rules: FileCheckRuleItem[] = [];
  // 用户配置的规则
  (inputRules || []).forEach((ruleType) => {
    const rule = getRuleItemJson(
      ruleType,
      ruleForm,
      formValues?.[`${ruleType}`],
      checkMethods?.[`${ruleType}`],
      true,
      ruleIds?.[`${ruleType}`],
    );
    rules.push(rule);
  });
  // 平台默认的质检，不影响结果，例如帧率、
  ruleForm?.forEach((ruleConfig) => {
    const { ruleType, checkMethods } = ruleConfig;
    const isDefault = ruleConfig.autoAdded && !inputRules?.includes(ruleType);
    if (isDefault) {
      const rule = getRuleItemJson(ruleType, [ruleConfig], '', checkMethods?.[0], false);
      rules.push(rule);
    }
    // console.log({ isDefault, ruleConfig });
  });
  Object.entries(formValues || {}).forEach(([key, value]) => {
    // 特殊表单项处理：name、isRequire、
    if ([CheckType.Name, CheckType.FileName, CheckType.IsRequired, CheckType.FileNameDesc].includes(key as CheckType)) {
      Object.assign(checkItem, { [`${key}`]: value });
    }
  });
  // FILE_RULES_OF_DEFAULT_SHOW
  Object.assign(checkItem, { rules, id: item.id });
  return checkItem;
}

/**
 * checkValue格式化:  数据库json数据 => 前端展示数据
 * @param ruleType checkValue的类型标识
 * @param  value
 * @param  ruleForm 动态规则
 * @returns formattedValue
 */
const formatCheckValueForFrontEnd = (ruleType, value, ruleForm?: RuleItemProps): any => {
  //
  if (!value) {
    return '';
  }
  switch (ruleType) {
    case CheckType.Resolution:
      const format = isObject(value) ? value : JSON.parse(value);
      return { x: format?.x, y: format?.y };
    case CheckType.FileSize:
      const unit = ruleForm?.checkValueInput?.props?.unit || '';
      const formatSize = formatByteByUnit(value, unit);
      return formatSize;
    default:
      return value;
  }
};
/**
 * checkValue格式化:  前端展示数据 => 数据库json数据
 * @param ruleType checkValue的类型标识
 * @param  value
 * @param  ruleForm 动态规则
 * @returns formattedValue 后端统一以string落库，前端与dcc服务端自行解析
 */
const formatCheckValueForBackEnd = (ruleType, value, ruleForm?: RuleItemProps): string => {
  //
  if (isNil(value)) {
    return '';
  }
  switch (ruleType) {
    case CheckType.Resolution:
      if (!value?.x || !value?.y) {
        // 对象结构的checkValue未正确配置，返回空
        return '';
      }
      const format = isString(value) ? value : JSON.stringify(value);
      return format;
    case CheckType.FileSize:
      const unit = ruleForm?.checkValueInput?.props?.unit;
      const formatSize = formatSizeToByte(value, unit);
      return formatSize?.toString() || '';
    default:
      return value;
  }
};
